import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import bgImage from '../../assets/images/background/bg-7.jpg';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Base URL for API calls
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://what-workout25-c959.vercel.app';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    try {
      // Detailed logging
      console.log('Forgot Password - Attempting to send reset link');
      console.log('Email:', email);
      console.log('Full API URL:', `${API_BASE_URL}/api/users/forgot-password`);

      const response = await fetch(`${API_BASE_URL}/api/users/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      // Log response details
      console.log('Response Status:', response.status);

      // Handle response text carefully
      const responseText = await response.text();
      console.log('Raw Response Text:', responseText);

      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        setMessage('An error occurred while processing the request.');
        return;
      }

      // Handle different response scenarios
      if (response.ok) {
        setIsSuccess(true);
        setMessage('If an account with that email exists, a password reset link has been sent.');
      } else {
        // Use error message from server or provide a default
        setIsSuccess(false);
        setMessage(data.message || 'Failed to process the password reset request.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setIsSuccess(false);
      setMessage('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Consistent styling with other pages
  const buttonStyle = {
    padding: '15px 30px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    textDecoration: 'none',
    textTransform: 'uppercase' as const,
    letterSpacing: '3px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '0',
    transition: 'all 0.3s ease',
    margin: '5px',
    display: 'inline-block',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 15px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontSize: '14px',
    marginBottom: '15px',
    fontFamily: "'Montserrat', sans-serif",
  };

  return (
    <div style={{
      backgroundImage: `url(${bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'white',
    }}>
      <div style={{
        maxWidth: '400px',
        width: '100%',
        padding: '30px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <h2 style={{
          fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
          fontWeight: '700',
          fontSize: '36px',
          textTransform: 'uppercase',
          letterSpacing: '5px',
          color: '#ffffff',
          marginBottom: '20px',
          textAlign: 'center',
        }}>
          Reset Password
        </h2>
        
        {message && (
          <div style={{
            backgroundColor: isSuccess 
              ? 'rgba(40, 167, 69, 0.7)' 
              : 'rgba(220, 53, 69, 0.7)',
            color: 'white',
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '4px',
            textAlign: 'center',
          }}>
            {message}
          </div>
        )}
        
        {!isSuccess ? (
          <form onSubmit={handleSubmit}>
            <p style={{ 
              marginBottom: '20px', 
              textAlign: 'center',
              color: 'rgba(255,255,255,0.7)'
            }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              required
            />
            
            <button
              type="submit"
              disabled={loading}
              style={{
                ...buttonStyle,
                width: '100%',
                backgroundColor: loading 
                  ? 'rgba(150, 150, 150, 0.25)' 
                  : 'rgba(255, 255, 255, 0.25)',
                margin: '10px 0 20px 0',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'SENDING...' : 'SEND RESET LINK'}
            </button>
          </form>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: '20px' }}>
              Check your email for the password reset link. 
              The link will expire in 1 hour.
            </p>
          </div>
        )}
        
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigate('/profile')}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline',
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;